import React from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { ContentCopy, Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { introductionFeeCopyVars } from '../config/plans';
import {
  partnerLinkSx,
  partnerOutlineBtnSx,
  partnerOxbloodBtnSx,
} from './PartnerListChrome';
import { DOCUMENT, documentEyebrowSx } from '../constants/documentTheme';
import { ALERT, formatMoney } from '../utils/partnerListUtils';

const detailSectionSx = {
  fontFamily: DOCUMENT.display,
  fontSize: 22,
  fontWeight: 400,
  pb: 1.25,
  mb: 0,
  borderBottom: `1px solid ${DOCUMENT.ink}`,
};

const factGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  borderBottom: `1px solid ${DOCUMENT.rule}`,
};

const metaSx = { fontSize: 11.5, color: DOCUMENT.ink3, display: 'block', mt: 0.35 };

const mailLinkSx = {
  ...metaSx,
  color: DOCUMENT.ink2,
  textDecoration: 'underline',
  textDecorationColor: DOCUMENT.ruleHard,
  '&:hover': { color: DOCUMENT.oxblood, textDecorationColor: DOCUMENT.oxblood },
};

const textLinkSx = {
  fontWeight: 400,
  fontSize: 15,
  display: 'block',
  background: 'none',
  border: 0,
  p: 0,
  cursor: 'pointer',
  textAlign: 'left',
  color: DOCUMENT.ink,
  textDecoration: 'underline',
  textDecorationColor: DOCUMENT.ruleHard,
  '&:hover': { color: DOCUMENT.oxblood, textDecorationColor: DOCUMENT.oxblood },
};

const DetailFact = ({ label, children, wide = false }) => (
  <Box
    sx={{
      py: 1.75,
      pr: 2,
      borderTop: `1px solid ${DOCUMENT.rule}`,
      gridColumn: wide ? '1 / -1' : 'auto',
    }}
  >
    <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.ink3, mb: 0.75 }}>{label}</Typography>
    {children}
  </Box>
);

const getPassengers = (booking) => {
  if (!booking?.travelers) return [];
  try {
    if (typeof booking.travelers === 'string') {
      return JSON.parse(booking.travelers);
    }
    return Array.isArray(booking.travelers) ? booking.travelers : [];
  } catch (e) {
    return [];
  }
};

const customerMailto = (booking) => {
  if (!booking?.user_email) return null;
  const subject = encodeURIComponent(
    [booking.booking_reference, booking.package_title].filter(Boolean).join(' — ')
  );
  return `mailto:${booking.user_email}?subject=${subject}`;
};

const PartnerBookingDetailsDialog = ({
  open,
  booking,
  onClose,
  onPackageClick,
  onMessage,
  onConfirm,
  onCopyPaymentLink,
  messagingBusy = false,
  hideMessage = false,
}) => {
  const { t } = useTranslation();

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return t('partnerLists.statusAwaiting');
      case 'partner_confirmed': return t('partnerLists.statusConfirmedPaid');
      case 'payment_pending': return t('partnerLists.statusConfirmedPaid');
      case 'paid': return t('partnerLists.statusPaid');
      case 'confirmed':
      case 'completed': return t('partnerLists.statusComplete');
      case 'cancelled': return t('partnerLists.statusCancelled');
      default: return status;
    }
  };

  if (!booking) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: DOCUMENT.canvas,
          color: DOCUMENT.ink,
          fontFamily: DOCUMENT.sans,
          borderRadius: 0,
          boxShadow: 'none',
          border: `1px solid ${DOCUMENT.ruleHard}`,
        },
      }}
    >
      <Box
        data-testid="booking-details-dialog"
        sx={{
          bgcolor: DOCUMENT.oxblood,
          color: DOCUMENT.ivory,
          px: 3,
          py: 2.25,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ ...documentEyebrowSx, color: '#C9A961', mb: 0.75 }}>
            {booking.booking_reference}
          </Typography>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 28, fontWeight: 400, lineHeight: 1.1 }}>
            {t('partnerLists.bookingDetails')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: DOCUMENT.ivory, mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 0, bgcolor: DOCUMENT.canvas }}>
        <Box sx={{ px: 3, pt: 3, pb: 1.5 }}>
          <Typography sx={detailSectionSx}>{t('partnerLists.overview')}</Typography>
          <Box sx={factGridSx}>
            <DetailFact label={t('partnerLists.colPackage')}>
              {onPackageClick ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => onPackageClick(booking)}
                  sx={textLinkSx}
                >
                  {booking.package_title}
                </Box>
              ) : (
                <Typography sx={{ fontSize: 15 }}>{booking.package_title}</Typography>
              )}
              <Typography sx={metaSx}>
                {[
                  booking.package_trip_code,
                  booking.destination,
                  booking.duration_days != null
                    ? `${booking.duration_days} ${t('partnerLists.daysWord')}`
                    : null,
                ].filter(Boolean).join(' · ')}
              </Typography>
            </DetailFact>
            <DetailFact label={t('partnerLists.colStatus')}>
              <Typography sx={{ fontSize: 15, color: booking.status === 'pending' ? ALERT : DOCUMENT.ink }}>
                {getStatusLabel(booking.status)}
              </Typography>
            </DetailFact>
            <DetailFact label={t('partnerLists.bookedBy')}>
              <Typography sx={{ fontSize: 15 }}>{booking.user_name}</Typography>
              {booking.user_email ? (
                <Box component="a" href={customerMailto(booking)} sx={{ ...mailLinkSx, mt: 0.5 }}>
                  {booking.user_email}
                </Box>
              ) : null}
            </DetailFact>
            <DetailFact label={t('partnerLists.bookedOn')}>
              <Typography sx={{ fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
                {booking.created_at
                  ? format(new Date(booking.created_at), 'd MMMM yyyy, h:mm a')
                  : t('partnerLists.notSet')}
              </Typography>
            </DetailFact>
            {booking.partner_confirmation_id && (
              <DetailFact label={t('partnerLists.confirmationId')}>
                <Typography sx={{ fontSize: 15 }}>{booking.partner_confirmation_id}</Typography>
              </DetailFact>
            )}
            {booking.special_requests && (
              <DetailFact label={t('partnerLists.specialRequests')} wide>
                <Typography sx={{ fontSize: 14.5, color: DOCUMENT.ink2 }}>{booking.special_requests}</Typography>
              </DetailFact>
            )}
            {booking.partner_notes && (
              <DetailFact label={t('partnerLists.notes')} wide>
                <Typography sx={{ fontSize: 14.5, color: DOCUMENT.ink2 }}>{booking.partner_notes}</Typography>
              </DetailFact>
            )}
          </Box>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 1.5 }}>
          <Typography sx={detailSectionSx}>{t('partnerLists.colDates')}</Typography>
          <Box sx={factGridSx}>
            <DetailFact label={t('partnerLists.departure')}>
              <Typography sx={{ fontSize: 18, fontFamily: DOCUMENT.display, fontVariantNumeric: 'tabular-nums' }}>
                {booking.travel_date
                  ? format(new Date(booking.travel_date), 'd MMMM yyyy')
                  : t('partnerLists.notSet')}
              </Typography>
            </DetailFact>
            <DetailFact label={t('partnerLists.return')}>
              <Typography sx={{ fontSize: 18, fontFamily: DOCUMENT.display, fontVariantNumeric: 'tabular-nums' }}>
                {booking.return_date
                  ? format(new Date(booking.return_date), 'd MMMM yyyy')
                  : t('partnerLists.notSet')}
              </Typography>
            </DetailFact>
          </Box>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 1.5 }}>
          <Typography sx={detailSectionSx}>
            {t('partnerLists.passengersCount', { count: booking.num_travelers || 1 })}
          </Typography>
          {getPassengers(booking).length > 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0 }}>
              {getPassengers(booking).map((passenger, index) => (
                <Box
                  key={`${passenger.passportNumber || passenger.email || index}`}
                  sx={{ py: 1.75, pr: 2, borderTop: `1px solid ${DOCUMENT.rule}` }}
                >
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, mb: 0.75 }}>
                    {[passenger.firstName || passenger.first_name, passenger.lastName || passenger.last_name].filter(Boolean).join(' ')}
                  </Typography>
                  {passenger.email && <Typography sx={metaSx}>{passenger.email}</Typography>}
                  {passenger.phone && <Typography sx={metaSx}>{passenger.phone}</Typography>}
                  {passenger.dateOfBirth && (
                    <Typography sx={metaSx}>{t('partnerLists.dob')}: {passenger.dateOfBirth}</Typography>
                  )}
                  {passenger.nationality && (
                    <Typography sx={metaSx}>{t('partnerLists.nationality')}: {passenger.nationality}</Typography>
                  )}
                  {passenger.passportNumber && (
                    <Typography sx={metaSx}>
                      {t('partnerLists.passport')}: {passenger.passportNumber}
                      {passenger.passportExpiry ? ` · ${passenger.passportExpiry}` : ''}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, py: 1 }}>
              {t('partnerLists.noPassengerDetails', { count: booking.num_travelers || 1 })}
            </Typography>
          )}
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 1.5 }}>
          <Typography sx={detailSectionSx}>{t('partnerLists.pricingTitle')}</Typography>
          <Box sx={factGridSx}>
            <DetailFact label={t('partnerLists.basePerPerson')}>
              <Typography sx={{ fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(booking.base_price)}
              </Typography>
            </DetailFact>
            <DetailFact label={t('partnerLists.colTravellers')}>
              <Typography sx={{ fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>
                {booking.num_travelers || 1}
              </Typography>
            </DetailFact>
            <DetailFact label={t('partnerLists.totalAmount')}>
              <Typography sx={{ fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(booking.total_price)}
              </Typography>
            </DetailFact>
            <DetailFact label={t('partnerLists.platformCommission', { rate: booking.commission_rate || 10, ...introductionFeeCopyVars() })}>
              <Typography sx={{ fontSize: 18, fontVariantNumeric: 'tabular-nums', color: DOCUMENT.ink2 }}>
                −{formatMoney(booking.commission_amount)}
              </Typography>
            </DetailFact>
            <DetailFact label={t('partnerLists.yourPayout')} wide>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 32, fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(booking.partner_payout_amount)}
              </Typography>
            </DetailFact>
          </Box>
        </Box>

        {(booking.payment_link_url || booking.paid_at) && (
          <Box sx={{ px: 3, pt: 2, pb: 2 }}>
            <Typography sx={detailSectionSx}>{t('partnerLists.paymentInfo')}</Typography>
            {booking.payment_link_url && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={booking.payment_link_url}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: DOCUMENT.surface,
                      borderRadius: 0,
                      fontSize: 13,
                    },
                  }}
                />
                {onCopyPaymentLink ? (
                  <Button sx={partnerOutlineBtnSx} onClick={() => onCopyPaymentLink(booking.payment_link_url)}>
                    <ContentCopy sx={{ fontSize: 16 }} />
                  </Button>
                ) : null}
              </Box>
            )}
            {booking.paid_at && (
              <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>
                {t('partnerLists.paidOn', {
                  date: format(new Date(booking.paid_at), 'd MMMM yyyy, h:mm a'),
                })}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${DOCUMENT.ruleHard}`,
          bgcolor: DOCUMENT.surface,
          gap: 2,
          justifyContent: 'flex-end',
        }}
      >
        <Box component="button" type="button" sx={partnerLinkSx} onClick={onClose}>
          {t('partnerLists.close')}
        </Box>
        {booking.user_email && (
          <Button component="a" href={customerMailto(booking)} sx={partnerOutlineBtnSx}>
            {t('partnerLists.emailCustomer')}
          </Button>
        )}
        {!hideMessage && onMessage ? (
          <Box
            component="button"
            type="button"
            sx={{
              ...partnerLinkSx,
              opacity: messagingBusy ? 0.5 : 1,
              pointerEvents: messagingBusy ? 'none' : 'auto',
            }}
            onClick={() => onMessage(booking)}
          >
            {t('partnerLists.messageInquiry')}
          </Box>
        ) : null}
        {booking.status === 'pending' && onConfirm ? (
          <Button
            sx={partnerOxbloodBtnSx}
            onClick={() => onConfirm(booking)}
          >
            {t('partnerLists.confirmBooking')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default PartnerBookingDetailsDialog;
